import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tosPreview } from '../../../utils/tosImage';

export const Lightbox: React.FC<{
  urls: string[];
  index: number;
  onClose: () => void;
  onChange: (i: number) => void;
  selectedImageUrl?: string;
  onSelect?: (url: string) => void;
  selectedUrls?: string[];
  onMultiSelect?: (url: string) => void;
  maxSelectable?: number;
}> = ({ urls, index, onClose, onChange, selectedImageUrl, onSelect, selectedUrls, onMultiSelect, maxSelectable }) => {
  const { t } = useTranslation('projectDetail');
  const prev = () => onChange((index - 1 + urls.length) % urls.length);
  const next = () => onChange((index + 1) % urls.length);
  const currentUrl = urls[index];
  const [imageLoading, setImageLoading] = useState(true);
  useEffect(() => { setImageLoading(true); }, [currentUrl]);
  const isSelected = selectedImageUrl === currentUrl;
  const isMultiSelected = selectedUrls?.includes(currentUrl) ?? false;
  const multiSelectDisabled = !isMultiSelected && !!maxSelectable && (selectedUrls?.length ?? 0) >= maxSelectable;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(currentUrl);
      const blob = await res.blob();
      const ext = blob.type.split('/')[1] || 'jpg';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `image-${index + 1}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(currentUrl, '_blank');
    }
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const btnStyle: React.CSSProperties = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
    fontSize: '2rem', width: '48px', height: '64px', cursor: 'pointer',
    borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    userSelect: 'none',
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {urls.length > 1 && (
        <button style={{ ...btnStyle, left: '16px' }} onClick={(e) => { e.stopPropagation(); prev(); }}>&#8249;</button>
      )}
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {imageLoading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <div style={{ width: 36, height: 36, border: '3px solid rgba(255,255,255,0.2)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
          </div>
        )}
        <img
          src={tosPreview(urls[index])}
          alt={`preview-${index + 1}`}
          onLoad={() => setImageLoading(false)}
          style={{ maxWidth: '88vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain', opacity: imageLoading ? 0.3 : 1, transition: 'opacity 0.2s' }}
        />
      </div>
      {urls.length > 1 && (
        <button style={{ ...btnStyle, right: '16px' }} onClick={(e) => { e.stopPropagation(); next(); }}>&#8250;</button>
      )}
      <div style={{ position: 'absolute', bottom: '16px', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{index + 1} / {urls.length}</span>
        <button
          onClick={handleDownload}
          style={{
            color: 'white', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: '6px', padding: '4px 14px', fontSize: '0.82rem', cursor: 'pointer',
          }}
        >
          {t('lightbox.download')}
        </button>
        {onSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(currentUrl); }}
            style={{
              color: isSelected ? 'var(--success)' : 'white',
              background: isSelected ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.18)',
              border: `1px solid ${isSelected ? 'var(--success)' : 'rgba(255,255,255,0.35)'}`,
              borderRadius: '6px', padding: '4px 14px', fontSize: '0.82rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            {isSelected ? t('lightbox.selected') : t('lightbox.select')}
          </button>
        )}
        {onMultiSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); if (!multiSelectDisabled) onMultiSelect(currentUrl); }}
            style={{
              color: isMultiSelected ? 'var(--success)' : multiSelectDisabled ? 'rgba(255,255,255,0.3)' : 'white',
              background: isMultiSelected ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.18)',
              border: `1px solid ${isMultiSelected ? 'var(--success)' : multiSelectDisabled ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.35)'}`,
              borderRadius: '6px', padding: '4px 14px', fontSize: '0.82rem',
              cursor: multiSelectDisabled ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px',
            }}
          >
            {isMultiSelected ? t('lightbox.multiSelected', { index: (selectedUrls?.indexOf(currentUrl) ?? 0) + 1 }) : multiSelectDisabled ? t('lightbox.maxReached') : t('lightbox.select')}
          </button>
        )}
      </div>
    </div>
  );
};
